import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { CalendarRange, ChevronRight, LucideAngularModule, RefreshCw, Search, Trash2 } from 'lucide-angular';
import { finalize } from 'rxjs';
import { environment } from '../../environments/environment';
import { AdminApiError } from '../core/admin-api';
import { isValidAdminDateRange, toAdminApiDate } from '../core/admin-date';
import { AdminDeletionRecord } from '../core/models';
import { AdminApiService } from '../data-access/admin-api.service';

type RemovalFilter = 'ADMIN' | 'USER' | 'ADMIN,USER';

@Component({
  selector: 'app-deletion-records',
  standalone: true,
  imports: [FormsModule, RouterLink, LucideAngularModule],
  template: `
    <section class="page-heading"><div><span class="eyebrow">AUDIT LOG</span><h1>刪除紀錄</h1><p>依日期與刪除來源查詢活動移除稽核資料。</p></div><div class="summary-pills danger-summary"><span><b>{{ records().length }}</b>筆刪除紀錄</span></div></section>
    <section class="filter-panel compact">
      <div class="filter-grid">
        <label class="field search-field"><span>活動或刪除者</span><div><lucide-angular [img]="Search" /><input [(ngModel)]="keyword" placeholder="輸入關鍵字篩選"></div></label>
        <label class="field"><span>刪除日期起日</span><div><lucide-angular [img]="CalendarRange" /><input type="date" [(ngModel)]="startDate"></div></label>
        <label class="field"><span>刪除日期迄日</span><div><lucide-angular [img]="CalendarRange" /><input type="date" [(ngModel)]="endDate"></div></label>
        <label class="field"><span>刪除來源</span><select [(ngModel)]="removedByType"><option value="ADMIN,USER">全部來源</option><option value="ADMIN">後台管理者</option><option value="USER">活動主辦方</option></select></label>
      </div>
      <div class="modal-actions"><button class="button primary" [disabled]="loading()" (click)="loadRecords()"><lucide-angular [img]="Search" />查詢紀錄</button></div>
      @if (queryError()) { <p class="form-error">{{ queryError() }}</p> }
    </section>
    <section class="data-card">
      <div class="card-toolbar"><div><h2>異動紀錄</h2><span>共 {{ filteredRecords().length }} 筆結果</span></div></div>
      @if (loading()) {
        <div class="empty-state"><span>…</span><h3>正在查詢刪除紀錄</h3><p>請稍候。</p></div>
      } @else if (error()) {
        <div class="empty-state"><span>!</span><h3>刪除紀錄查詢失敗</h3><p>{{ error() }}</p><button class="button secondary" (click)="loadRecords()"><lucide-angular [img]="RefreshCw" />重新查詢</button></div>
      } @else if (filteredRecords().length) {
        <div class="desktop-table"><table><thead><tr><th>活動</th><th>刪除來源</th><th>刪除者</th><th>刪除日期</th><th>刪除原因</th><th>連結</th></tr></thead><tbody>
          @for (record of filteredRecords(); track record.gameId + record.removeDate) {
            <tr>
              <td><a class="game-cell" [routerLink]="['/games', record.gameId]"><img [src]="imageUrl(record)" [alt]="record.gameName" (error)="usePlaceholder($event)"><span><strong>{{ record.gameName }}</strong><small>{{ record.gameId }}</small></span></a></td>
              <td><span class="status-badge" [attr.data-status]="record.removedByType">{{ sourceLabel(record.removedByType) }}</span></td>
              <td><strong class="table-primary">{{ record.deletedName }}</strong><small>{{ record.removedById || '無 ID' }}</small></td>
              <td>{{ record.removeDate }}</td><td><span class="reason-cell">{{ record.removedReason || '未提供原因' }}</span></td>
              <td><a class="row-action" [routerLink]="['/games', record.gameId]" aria-label="後台詳細"><lucide-angular [img]="ChevronRight" /></a></td>
            </tr>
          }
        </tbody></table></div>
        <div class="mobile-list">@for (record of filteredRecords(); track record.gameId + record.removeDate) { <article class="record-mobile"><div class="record-icon"><lucide-angular [img]="Trash2" /></div><div><h3><a [routerLink]="['/games', record.gameId]">{{ record.gameName }}</a></h3><p>{{ record.removedReason || '未提供原因' }}</p><small>{{ sourceLabel(record.removedByType) }} · {{ record.deletedName }} · {{ record.removeDate }}</small></div><a [routerLink]="['/games', record.gameId]" aria-label="後台詳細"><lucide-angular [img]="ChevronRight" /></a></article> }</div>
      } @else {
        <div class="empty-state"><span>0</span><h3>沒有刪除紀錄</h3><p>目前日期與刪除來源沒有符合的資料。</p></div>
      }
    </section>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeletionRecordsComponent implements OnInit {
  private readonly api = inject(AdminApiService);
  readonly records = signal<AdminDeletionRecord[]>([]);
  readonly loading = signal(false);
  readonly error = signal('');
  readonly queryError = signal('');
  keyword = '';
  startDate = this.defaultStartDate();
  endDate = this.inputDate(new Date());
  removedByType: RemovalFilter = 'ADMIN,USER';
  readonly Search = Search; readonly CalendarRange = CalendarRange; readonly ChevronRight = ChevronRight; readonly Trash2 = Trash2; readonly RefreshCw = RefreshCw;

  ngOnInit(): void { this.loadRecords(); }

  loadRecords(): void {
    if (this.loading()) return;
    if (!isValidAdminDateRange(this.startDate, this.endDate)) { this.queryError.set('請輸入有效的刪除日期區間，且起日不可晚於迄日。'); return; }
    this.queryError.set(''); this.error.set(''); this.loading.set(true);
    this.api.getDeletionRecords(toAdminApiDate(this.startDate), toAdminApiDate(this.endDate), this.removedByType).pipe(
      finalize(() => this.loading.set(false)),
    ).subscribe({
      next: records => this.records.set(records),
      error: (error: AdminApiError) => this.error.set(error.message),
    });
  }

  filteredRecords(): AdminDeletionRecord[] {
    const keyword = this.keyword.trim().toLowerCase();
    return this.records().filter(record => !keyword || `${record.gameName} ${record.deletedName} ${record.removedById} ${record.removedReason ?? ''}`.toLowerCase().includes(keyword));
  }
  sourceLabel(source: 'ADMIN' | 'USER'): string { return source === 'ADMIN' ? '後台管理者' : '活動主辦方'; }
  imageUrl(record: AdminDeletionRecord): string { return record.gameImage?.imageId ? `${environment.imagesUrl}/games/${record.gameId}/images/${record.gameImage.imageId}` : this.placeholder; }
  usePlaceholder(event: Event): void { (event.target as HTMLImageElement).src = this.placeholder; }

  private readonly placeholder = 'assets/logos/crewjam/cj.svg';
  private defaultStartDate(): string { const date = new Date(); date.setMonth(date.getMonth() - 1); return this.inputDate(date); }
  private inputDate(date: Date): string { return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`; }
}
